/**
 * The three charts in the top row: the overall-score gauge, the coxcomb and
 * the dumbbell gap chart. A port of app/public/dashboard.js, emitting the
 * same markup, attributes and inline styles.
 *
 * Inline style objects list their properties in the order legacy set them.
 * That is not cosmetic: the browser serialises declarations in insertion
 * order, and the parity harness compares that serialisation.
 *
 * Entry animations are declared inline on each element, as in legacy, so the
 * one rule that matters is that hover must never REMOUNT anything -- a new
 * node replays its animation, which is what made the legacy dashboard look
 * like it refreshed when the mouse crossed a wedge before that was fixed.
 * React keeps nodes across re-renders as long as keys are stable, and a
 * re-render with unchanged style values writes nothing to the DOM, so only
 * the properties hover actually changes are touched.
 */

import {
  BAND, SWOOSHES, SWIRL_N, SWIRL_TWIST, SHORT, KEYS,
  point, wedgePath, pctOf, comparisonAbsenceReason,
} from './constants.js';

/* ── Overall score device ────────────────────────────────────────── */

function CompareRow({ label, pct, colour, kind, delay }) {
  return (
    <div className="compare-row">
      <span className={`compare-label ${kind}`}>{label}</span>
      <span className="compare-track">
        <span
          className="compare-fill"
          style={{
            width: `${pct}%`,
            background: colour,
            transformOrigin: 'left center',
            animation: `barGrow .55s cubic-bezier(.16,1,.3,1) ${delay}s both`,
          }}
        />
      </span>
      <span className={`compare-val ${kind}`}>{`${pct}%`}</span>
    </div>
  );
}

export function Gauge({ report, benchmark }) {
  const band = BAND[report.overallBand];

  const bladeR = 86;
  const Rout = 76;
  const Rin = 54;
  const size = Math.round(bladeR * 2 + 14);
  const cx = size / 2;
  const fill = (report.overall - 1) / 3;

  // The overall comparison is against the WHOLE cohort, not the persona
  // peer group. `cohort.count` already includes this business, so it is the
  // number the "All N businesses assessed" line is describing.
  const cohort = benchmark?.cohort;
  let comparison;
  if (cohort && cohort.count > 1) {
    const youPct = report.overallPercent;
    const avgOverall = KEYS.map((k) => cohort.scores[k]).filter((v) => v !== null);
    const avgPct = Math.round(pctOf(avgOverall.reduce((a, b) => a + b, 0) / avgOverall.length));
    const delta = youPct - avgPct;
    const up = delta >= 0;
    comparison = (
      <>
        <div className={`delta-line ${up ? 'delta-up' : 'delta-down'}`} style={{ animation: 'scoreReveal .4s ease-out 1.1s both' }}>
          {`${up ? '↑' : '↓'} ${Math.abs(delta)} points ${up ? 'above' : 'below'} average`}
        </div>
        <div className="compare-wrap" style={{ animation: 'scoreReveal .4s ease-out 1.25s both' }}>
          <CompareRow label="You" pct={youPct} colour={band.chart} kind="you" delay={1.35} />
          <CompareRow label="Average" pct={avgPct} colour="#B9CCC4" kind="avg" delay={1.45} />
          <div className="compare-foot">{`All ${cohort.count} businesses assessed`}</div>
        </div>
      </>
    );
  } else {
    comparison = <div className="absence-note">{comparisonAbsenceReason(report, benchmark)}</div>;
  }

  return (
    <div className="chart-col chart-col-1">
      <div className="chart-label">Overall score</div>
      <div className="chart-body">
        <div className="gauge-wrap">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* 96 twisted radial lines; those below the score take the band colour. */}
            <g>
              {Array.from({ length: SWIRL_N }, (_, i) => {
                const f = i / SWIRL_N;
                const a1 = ((-90 + 360 * f) * Math.PI) / 180;
                const a2 = ((-90 + 360 * f + SWIRL_TWIST) * Math.PI) / 180;
                return (
                  <line
                    key={i}
                    x1={(cx + Rout * Math.cos(a1)).toFixed(1)}
                    y1={(cx + Rout * Math.sin(a1)).toFixed(1)}
                    x2={(cx + Rin * Math.cos(a2)).toFixed(1)}
                    y2={(cx + Rin * Math.sin(a2)).toFixed(1)}
                    stroke={f <= fill ? band.chart : '#CFE3DD'}
                    strokeWidth={f <= fill ? 1.4 : 1}
                    style={{ opacity: 0, animation: `scoreReveal .3s ease-out ${(0.15 + f * 0.5).toFixed(2)}s forwards` }}
                  />
                );
              })}
            </g>
            <circle cx={cx} cy={cx} r={(Rin - 3).toFixed(1)} fill="#FFFFFF" />
            {/* The logo's own blades fly out and take the band colour at 50%. */}
            <g style={{ transformOrigin: '50% 50%', animation: 'swooshFly 1.5s cubic-bezier(.45,0,.18,1) 0s both' }}>
              <g transform={`translate(${cx},${cx}) scale(74)`}>
                {SWOOSHES.map((s, i) => (
                  <path
                    key={s.fill}
                    d={s.d}
                    fill={s.fill}
                    style={{
                      '--logoFill': s.fill,
                      '--bandFill': band.chart,
                      animation: `swooshTint 1.5s cubic-bezier(.5,0,.2,1) ${(i * 0.05).toFixed(2)}s both`,
                    }}
                  />
                ))}
              </g>
            </g>
          </svg>
          <div className="gauge-centre">
            {/* §1.4.1 — a band colour never carries text at its own value.
                `edge` is the contrast-checked variant; every band clears 3:1
                at this size. */}
            <div className="gauge-score" style={{ color: band.edge }}>{`${report.overallPercent}%`}</div>
            <div className="gauge-caption">maturity</div>
          </div>
        </div>
        <div className="band-pill" style={{ background: band.fill, color: band.on }}>{report.overallBand}</div>
        {comparison}
      </div>
    </div>
  );
}

/* ── Coxcomb ─────────────────────────────────────────────────────── */

export function Coxcomb({ report, hover, onHover }) {
  const R = 104;
  const minR = 20;
  const pad = 56;
  const centre = R + 18;
  const svgSize = centre * 2;

  const items = KEYS.map((key, i) => {
    const score = report.scores[key];
    if (score === null) return null;
    const a0 = -90 + i * 72;
    const mid = a0 + 36;
    const r = minR + (score / 4) * (R - minR);
    return {
      index: i,
      key,
      score,
      a0,
      a1: a0 + 72,
      r,
      band: BAND[report.bands[key]],
      rest: point(centre + pad, centre + pad, R + 40, mid),
      lifted: point(centre + pad, centre + pad, R + 43, mid),
      // Where the wedge sits when raised, and where its tooltip belongs.
      lift: point(0, 0, 3, mid),
      tipAt: point(centre + pad, centre + pad, r * 0.65 + 18, mid),
    };
  }).filter(Boolean);

  const active = items.find((item) => item.index === hover);

  return (
    <div className="chart-col chart-col-2">
      <div className="chart-label">The shape of your maturity</div>
      <div className="chart-body">
        <div className="cox-wrap" style={{ width: `${svgSize + pad * 2}px`, height: `${svgSize + pad * 2}px` }}>
          <svg
            width={svgSize}
            height={svgSize}
            viewBox={`0 0 ${svgSize} ${svgSize}`}
            style={{ position: 'absolute', left: `${pad}px`, top: `${pad}px` }}
          >
            {[R, Math.round(R * 0.67), Math.round(R * 0.34)].map((r, i) => (
              <circle
                key={`ring-${r}`}
                cx={centre}
                cy={centre}
                r={r}
                fill="none"
                stroke="#B9CCC4"
                strokeWidth={1.5}
                style={{
                  transformOrigin: `${centre}px ${centre}px`,
                  opacity: 0,
                  animation: `ringGrow .45s cubic-bezier(.16,1,.3,1) ${(0.15 + i * 0.07).toFixed(2)}s forwards`,
                }}
              />
            ))}
            {KEYS.map((key, i) => {
              const pt = point(centre, centre, R + 12, -90 + i * 72);
              return (
                <line
                  key={`spoke-${key}`}
                  x1={centre}
                  y1={centre}
                  x2={pt.x.toFixed(1)}
                  y2={pt.y.toFixed(1)}
                  stroke="#B9CCC4"
                  strokeWidth={1.5}
                  style={{ opacity: 0, animation: `scoreReveal .3s ease-out ${(0.3 + i * 0.04).toFixed(2)}s forwards` }}
                />
              );
            })}
            {items.map((item) => {
              const hot = item.index === hover;
              return (
                <g
                  key={`petal-${item.key}`}
                  style={{
                    transformOrigin: `${centre}px ${centre}px`,
                    opacity: 0,
                    animation: `petalGrow .55s cubic-bezier(.16,1,.3,1) ${(0.45 + item.index * 0.07).toFixed(2)}s forwards`,
                  }}
                >
                  <path
                    d={wedgePath(centre, centre, item.r, item.a0, item.a1, minR * 0.55)}
                    fill={item.band.chart}
                    fillOpacity={hot ? '1' : '0.88'}
                    // Stroked in the contrast-checked edge colour so a pale
                    // Foundation wedge still has a legible boundary on white.
                    stroke={item.band.edge}
                    strokeWidth={1.25}
                    strokeLinejoin="miter"
                    shapeRendering="geometricPrecision"
                    style={{
                      cursor: 'pointer',
                      transform: hot
                        ? `translate(${item.lift.x.toFixed(2)}px,${item.lift.y.toFixed(2)}px)`
                        : 'translate(0px,0px)',
                      transition: 'transform .18s cubic-bezier(.16,1,.3,1), fill-opacity .18s',
                    }}
                    onMouseEnter={() => onHover(item.index)}
                    onMouseLeave={() => onHover(null)}
                  />
                </g>
              );
            })}
          </svg>

          {items.map((item) => {
            const hot = item.index === hover;
            const at = hot ? item.lifted : item.rest;
            return (
              <div
                key={`label-${item.key}`}
                className={`cox-label${hot ? ' hot' : ''}`}
                style={{
                  left: `${at.x.toFixed(1)}px`,
                  top: `${at.y.toFixed(1)}px`,
                  width: '66px',
                  animation: `scoreReveal .35s ease-out ${(0.6 + item.index * 0.07).toFixed(2)}s both`,
                }}
              >
                {SHORT[item.key]}
              </div>
            );
          })}

          <div
            className="chart-tooltip"
            hidden={!active}
            style={active ? { left: `${active.tipAt.x}px`, top: `${active.tipAt.y}px` } : undefined}
          >
            <div className="tooltip-name">{active ? report.dimensionLabels[active.key] : ''}</div>
            <div className="tooltip-value">{active ? `Score: ${active.score.toFixed(1)} / 4.0` : ''}</div>
          </div>
        </div>

        {/* §7.2 — every visualisation needs a text equivalent. */}
        <div className="sr-only">
          {KEYS
            .map((k) => `${report.dimensionLabels[k]}: ${report.scores[k] ?? 'not scored'} out of 4, ${report.bands[k] ?? 'no digital maturity level'}`)
            .join('. ')}
        </div>
      </div>
    </div>
  );
}

/* ── Dumbbell gap chart ──────────────────────────────────────────── */

export function GapChart({ report, benchmark, hover, onHover }) {
  const trackW = 230;

  // The per-pillar comparison is against SAME-PERSONA peers only: the two
  // branches ask different questions, so a cross-persona pillar average
  // would not compare like with like. Suppressed below the minimum cell size.
  const peers = benchmark?.peers;
  const hasBench = Boolean(peers?.available && peers.scores);

  return (
    <div className="chart-col chart-col-3">
      <div className="chart-label">Where the gap is</div>
      <div className="chart-body">
        <div className="gap-list">
          {KEYS.map((key, i) => {
            const score = report.scores[key];
            if (score === null) return null;

            const band = BAND[report.bands[key]];
            const bench = hasBench ? peers.scores[key] : null;
            const scoreX = (score / 4) * trackW;
            const benchX = hasBench ? (bench / 4) * trackW : null;
            const gap = hasBench ? score - bench : null;
            const up = !hasBench || gap >= 0;

            return (
              <div
                key={key}
                className={`gap-row${i === hover ? ' hot' : ''}`}
                style={{ animation: `rowIn .4s cubic-bezier(.16,1,.3,1) ${(0.8 + i * 0.08).toFixed(2)}s both` }}
                onMouseEnter={() => onHover(i)}
                onMouseLeave={() => onHover(null)}
              >
                <div className="gap-dot" style={{ background: band.chart, border: `1.5px solid ${band.edge}` }} />
                <div className="gap-name">{report.dimensionLabels[key]}</div>
                <div className="gap-track-wrap">
                  <div className="gap-track" />
                  {hasBench && (
                    <>
                      <div
                        className="gap-connector"
                        style={{
                          left: `${Math.min(scoreX, benchX)}px`,
                          width: `${Math.abs(scoreX - benchX)}px`,
                          transformOrigin: 'left center',
                          animation: `gapConnector .38s cubic-bezier(.16,1,.3,1) ${(1.0 + i * 0.08).toFixed(2)}s both`,
                        }}
                      />
                      <div
                        className="gap-bench-dot"
                        style={{ left: `${benchX}px`, animation: `gapDotIn .4s cubic-bezier(.16,1,.3,1) ${(0.9 + i * 0.08).toFixed(2)}s both` }}
                      />
                    </>
                  )}
                  <div
                    className="gap-score-dot"
                    style={{ left: `${scoreX}px`, animation: `gapDotIn .4s cubic-bezier(.16,1,.3,1) ${(0.9 + i * 0.08).toFixed(2)}s both` }}
                  />
                </div>
                <div
                  className={`gap-value ${up ? 'up' : 'down'}`}
                  style={{ animation: `scoreReveal .35s ease-out ${(1.05 + i * 0.08).toFixed(2)}s both` }}
                >
                  {hasBench ? `${up ? '+' : ''}${gap.toFixed(1)}` : score.toFixed(1)}
                </div>
              </div>
            );
          })}

          {!hasBench && <div className="absence-note">{comparisonAbsenceReason(report, benchmark)}</div>}

          <div className="gap-legend" style={{ animation: 'scoreReveal .4s ease-out 1.5s both' }}>
            {hasBench ? (
              <>
                <span className="legend-dot-business" />
                <span>Your score</span>
                <span className="legend-dot-bench" />
                <span>{`Businesses like yours (${peers.count})`}</span>
                {peers.indicative && <span> · indicative at this sample size</span>}
              </>
            ) : (
              <>
                <span className="legend-dot-business" />
                <span>Your score out of 4.0</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
